import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Crown, Zap, Sparkles, Gift, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { createDonation, subscribeTier, fetchTiers, type TierDef, type TierId } from '@/lib/donations';
import { googleSignInUrl } from '@/lib/auth';
import { cn } from '@/lib/cn';

const MIN_DONATION_EUR = 3;
const PRESETS = [3, 5, 10, 25, 50, 100];

export default function Donate() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [amount, setAmount] = useState(5);
  const [anonymous, setAnonymous] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [tiers, setTiers] = useState<TierDef[]>(DEFAULT_TIERS);

  useEffect(() => {
    fetchTiers().then(setTiers).catch(() => { /* keep defaults */ });
  }, []);

  async function donateOneTime() {
    setLoading(true);
    try {
      const r = await createDonation({
        amount_cents: Math.round(amount * 100),
        anonymous, message,
        locale: i18n.language.slice(0,2),
      });
      window.location.href = r.checkout_url;
    } catch (e: any) {
      alert(t('donate.error', { msg: e.message }));
    } finally {
      setLoading(false);
    }
  }

  async function subscribe(tier: TierId) {
    if (!user) {
      // Redirect to Google sign-in, return back to /donate
      window.location.href = googleSignInUrl();
      return;
    }
    setLoading(true);
    try {
      const r = await subscribeTier(tier, i18n.language.slice(0,2));
      window.location.href = r.checkout_url;
    } catch (e: any) {
      if (e.message === 'login_required') {
        window.location.href = googleSignInUrl();
        return;
      }
      alert(t('donate.error', { msg: e.message }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* HEADER */}
      <section className="section pb-12">
        <div className="container-narrow text-center">
          <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ duration: 0.4 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 pill-pixel bg-gold-500/10 border-gold-500/30 text-gold-300">
              <Heart className="w-4 h-4" /> {t('donate.tag')}
            </div>
            <h1 className="font-pixel text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight">
              {t('donate.title')}
            </h1>
            <p className="mt-4 text-kquest-300 text-lg max-w-2xl mx-auto">
              {t('donate.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ONE-TIME DONATION */}
      <section className="section pt-0">
        <div className="container-narrow max-w-3xl">
          <div className="kblock p-8 sm:p-10">
            <h2 className="font-pixel text-2xl sm:text-3xl font-bold mb-2 flex items-center gap-3">
              <Gift className="w-7 h-7 text-gold-400" /> {t('donate.onetime_title')}
            </h2>
            <p className="text-kquest-300 mb-6">{t('donate.onetime_desc')}</p>

            {/* Amount selector */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  onClick={() => setAmount(p)}
                  className={cn(
                    'px-3 py-3 font-pixel text-sm border-2 transition-all',
                    amount === p
                      ? 'bg-gold-500 text-kquest-950 border-gold-700 shadow-block-gold'
                      : 'bg-kquest-900 text-kquest-200 border-kquest-700 hover:border-kquest-500'
                  )}
                >
                  €{p}
                </button>
              ))}
            </div>

            {/* Custom amount */}
            <div className="mb-6">
              <label className="block font-pixel text-sm uppercase tracking-wider text-kquest-200 mb-2">
                {t('donate.amount_label')}
              </label>
              <div className="flex items-center gap-2">
                <span className="font-press text-2xl text-gold-400">€</span>
                <input
                  type="number"
                  min={MIN_DONATION_EUR}
                  max={1000}
                  step={1}
                  value={amount}
                  onChange={(e) => setAmount(Math.max(MIN_DONATION_EUR, Math.min(1000, parseInt(e.target.value, 10) || MIN_DONATION_EUR)))}
                  className="flex-1 bg-kquest-900 border-2 border-kquest-700 focus:border-gold-500 px-4 py-3 text-kquest-100 outline-none font-pixel text-lg"
                />
              </div>
            </div>

            {/* Message */}
            <div className="mb-6">
              <label className="block font-pixel text-sm uppercase tracking-wider text-kquest-200 mb-2">
                {t('donate.message_label')}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 280))}
                rows={2}
                placeholder={t('donate.message_placeholder')}
                className="w-full bg-kquest-900 border-2 border-kquest-700 focus:border-kquest-500 px-3 py-2 text-kquest-100 outline-none font-pixel text-sm resize-none"
              />
              <div className="mt-1 text-xs text-kquest-400 font-pixel">{message.length} / 280</div>
            </div>

            {/* Anonymous toggle */}
            <label className="flex items-center gap-3 mb-8 cursor-pointer">
              <input
                type="checkbox"
                checked={anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
                className="w-5 h-5 accent-kquest-500"
              />
              <span className="font-pixel text-sm text-kquest-200">{t('donate.anonymous_label')}</span>
            </label>

            {/* What you get */}
            <div className="block-purple p-4 mb-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-gold-300 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-pixel text-gold-300 mb-1">{t('donate.benefits_title')}</div>
                  <ul className="text-sm text-kquest-100 leading-relaxed list-disc list-inside space-y-1">
                    <li>{t('donate.benefit_premium')}</li>
                    <li>{t('donate.benefit_donor_badge')}</li>
                    <li>{t('donate.benefit_archive')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <button
              disabled={loading || amount < MIN_DONATION_EUR}
              onClick={donateOneTime}
              className="btn-gold w-full !py-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Heart className="w-5 h-5" />
              {loading ? t('donate.loading') : t('donate.donate_now', { amount: amount.toString() })}
            </button>

            <p className="mt-4 text-center text-xs text-kquest-400 font-pixel">
              {t('donate.fine_print')}
            </p>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="section bg-kquest-900/40">
        <div className="container-narrow">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="font-pixel text-3xl sm:text-4xl font-bold tracking-tight">{t('donate.tiers_title')}</h2>
            <p className="mt-4 text-kquest-300">{t('donate.tiers_subtitle')}</p>
            {!user && (
              <p className="mt-3 text-sm text-gold-400 font-pixel">
                {t('donate.tiers_login_hint')}
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => {
              const Icon = tier.id === 're' ? Crown : tier.id === 'cavaliere' ? Zap : Sparkles;
              const featured = tier.id === 'cavaliere';
              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className={cn(
                    'relative p-8 flex flex-col',
                    featured ? 'block-gold border-2 shadow-gold-glow scale-[1.02]' : 'kblock enchant-card'
                  )}
                >
                  {featured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-press bg-kquest-950 text-gold-300 border-2 border-gold-500 uppercase">
                      MOST POPULAR
                    </span>
                  )}
                  <Icon className={cn('w-10 h-10 mb-4', featured ? 'text-kquest-950' : 'text-gold-400')} strokeWidth={1.6} />
                  <h3 className={cn(
                    'font-pixel text-2xl font-bold mb-2',
                    featured ? 'text-kquest-950' : 'text-kquest-100'
                  )}>
                    {tier.name}
                  </h3>
                  <div className={cn(
                    'font-press text-3xl mb-1',
                    featured ? 'text-kquest-950' : 'text-gradient-enchant'
                  )}>
                    €{tier.monthly_eur}
                  </div>
                  <div className={cn(
                    'text-xs font-pixel mb-6',
                    featured ? 'text-kquest-900' : 'text-kquest-400'
                  )}>
                    {t('donate.per_month')}
                  </div>

                  <ul className={cn(
                    'space-y-2 mb-8 flex-1',
                    featured ? 'text-kquest-900' : 'text-kquest-200'
                  )}>
                    {tier.perks.map((p, j) => (
                      <li key={j} className="flex gap-2 text-sm">
                        <span className={featured ? 'text-kquest-950' : 'text-gold-400'}>✓</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    disabled={loading}
                    onClick={() => subscribe(tier.id)}
                    className={cn(
                      'w-full',
                      featured ? 'btn-purple' : 'btn-gold'
                    )}
                  >
                    {user
                      ? t('donate.subscribe_cta', { tier: tier.name })
                      : t('donate.signin_to_subscribe')}
                  </button>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <p className="text-xs text-kquest-400 font-pixel max-w-2xl mx-auto leading-relaxed">
              {t('donate.tiers_fine_print')}
            </p>
          </div>
        </div>
      </section>

      {/* YouTube reality note */}
      <section className="section">
        <div className="container-narrow max-w-3xl">
          <div className="kblock p-6 border-l-4 border-l-gold-500">
            <div className="flex items-start gap-3">
              <Heart className="w-5 h-5 text-gold-400 flex-shrink-0 mt-1" />
              <div>
                <div className="font-pixel text-lg text-gold-300 mb-2">{t('donate.youtube_note_title')}</div>
                <p className="text-sm text-kquest-200 leading-relaxed">
                  {t('donate.youtube_note_body')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const DEFAULT_TIERS: TierDef[] = [
  {
    id: 'apprendista', name: 'Apprendista', price_cents: 299, monthly_eur: 2.99,
    weight: 1, monthly_votes: 5, monthly_quests: 0, monthly_veto: 0,
    perks: [
      'Badge Apprendista sul sito + ruolo Discord',
      '5 voti gratis ogni mese (€2,50 di valore)',
      'Early access ai video 12h prima del pubblico',
      'Accesso al canale #members su Discord',
    ],
  },
  {
    id: 'cavaliere', name: 'Cavaliere', price_cents: 799, monthly_eur: 7.99,
    weight: 2, monthly_votes: 20, monthly_quests: 1, monthly_veto: 0,
    perks: [
      'Tutti i perk Apprendista',
      '20 voti gratis ogni mese (€10 di valore)',
      'Voto pesato ×2',
      '1 quest gratis ogni mese (€5 di valore)',
      'Early access 24h ai video',
      'Voti accesso uncut version (video lunghi 2h)',
      'Priorità reply su Discord',
      'Sondaggi sulle prossime regole/mod del server',
    ],
  },
  {
    id: 're', name: 'Re', price_cents: 1999, monthly_eur: 19.99,
    weight: 3, monthly_votes: 60, monthly_quests: 3, monthly_veto: 1,
    perks: [
      'Tutti i perk Cavaliere',
      '60 voti gratis ogni mese (€30 di valore)',
      'Voto pesato ×3',
      '3 quest gratis ogni mese (€15 di valore)',
      'Early access 48h ai video',
      'NPC col tuo nome sul server (visibile in ogni episodio)',
      'Nome scolpito su una build permanente',
      '👑 Veto power: 1 quest/mese pushata al top in classifica',
      'Q&A mensile riservato in voice Discord',
    ],
  },
];
